import * as Papa from 'papaparse';
import * as XLSX from 'xlsx';

export type ParsedRosterFile = {
  fileName: string;
  headers: string[];
  rows: string[][];
};

const ROSTER_EXTENSIONS = ['.csv', '.xls', '.xlsx'] as const;

export function isSupportedRosterFileName(fileName: string): boolean {
  const lower = fileName.toLowerCase();
  return ROSTER_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

function cellToString(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value).trim();
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  return String(value).trim();
}

function normalizeSheetRows(rawRows: unknown[][]): { headers: string[]; rows: string[][] } {
  const data = rawRows
    .map((row) => (Array.isArray(row) ? row.map(cellToString) : []))
    .filter((row) => row.some((cell) => cell.length > 0));

  if (data.length < 2) {
    throw new Error('The file must include a header row and at least one data row.');
  }

  const headers = data[0].map((h) => h.trim());
  const rows = data.slice(1).map((row) => headers.map((_header, i) => (row[i] ?? '').trim()));

  return { headers, rows };
}

async function parseCsvFile(file: File): Promise<{ headers: string[]; rows: string[][] }> {
  const text = await file.text();
  const parsed = Papa.parse<string[]>(text, {
    skipEmptyLines: true,
  });

  if (parsed.errors.length > 0) {
    throw new Error(parsed.errors[0]?.message ?? 'Could not parse CSV.');
  }

  return normalizeSheetRows(parsed.data as unknown[][]);
}

async function parseExcelFile(file: File): Promise<{ headers: string[]; rows: string[][] }> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) {
    throw new Error('The Excel file has no worksheets.');
  }

  const sheet = workbook.Sheets[sheetName];
  const rawRows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    defval: '',
    raw: false,
  }) as unknown[][];

  return normalizeSheetRows(rawRows);
}

export async function parseRosterFile(file: File): Promise<ParsedRosterFile> {
  const lower = file.name.toLowerCase();

  if (!isSupportedRosterFileName(file.name)) {
    throw new Error('Upload a .csv, .xls, or .xlsx roster file.');
  }

  let parsed: { headers: string[]; rows: string[][] };
  if (lower.endsWith('.csv')) {
    parsed = await parseCsvFile(file);
  } else {
    parsed = await parseExcelFile(file);
  }

  return {
    fileName: file.name,
    headers: parsed.headers,
    rows: parsed.rows,
  };
}
